import {
    delete_devengo_by_doc_id,
    delete_prorate_by_company_involved,
    get_account_plan_by_company_ordered,
    get_counterpart,
    get_devengo_batch,
    get_devengo_x_company_doc,
    get_doc_accruals,
    get_documents_involved,
    get_paginated_counterparts,
    get_prorates_x_company_involved,
    getCategories,
    hasNewDocsByCompany,
    save_devengo,
    save_prorate_tree,
} from "@/api/carteraAnalista";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { createTakeLatest } from "@/utils/takeLatest";

// Take-latest por concern (antes: counters de race-id ad-hoc). Si una llamada
// más nueva del mismo concern arranca antes de que la respuesta llegue, la
// vieja se descarta para no clobbear estado fresco con datos stale (caso
// típico: usuario cambia filtro dos veces rápido — la primera request, más
// lenta, llegaría después de la segunda). El error de una llamada stale se
// loguea pero no pisa error/loading del fetch vigente.
const runLatestCounterparts = createTakeLatest();
const runLatestAccounts = createTakeLatest();
const runLatestDocuments = createTakeLatest();
const runLatestCounterpartUpdate = createTakeLatest();

function dedupeAttribute(items) {
    const ids = items.map((item) => item.attr_id);
    return items.filter((item, i) => ids.indexOf(item.attr_id) === i);
}

function postProcessCounterparts(counterparts, categories) {
    return counterparts.map((counterpart) => {
        const attribute = { ...(counterpart.attribute ?? {}) };
        if (attribute[categories[0]]) {
            attribute[categories[0]] = dedupeAttribute(attribute[categories[0]]);
        }
        if (attribute[categories[1]]) {
            attribute[categories[1]] = dedupeAttribute(attribute[categories[1]]);
        }
        return {
            ...counterpart,
            allText: (counterpart.rut + " " + counterpart.alias).toUpperCase(),
            visible: true,
            attribute,
        };
    });
}

function normalizeCounterpartAccounts(counterpart, accounts) {
    if (!counterpart) {
        return {};
    }
    const calculatedAccounts = [];
    const calculatedMotherAccounts = [...(counterpart.mother_accounts ?? [])];

    let calculatedCategories = {};
    if (
        counterpart.doc_attributes &&
        typeof counterpart.doc_attributes === "object" &&
        !Array.isArray(counterpart.doc_attributes)
    ) {
        calculatedCategories = JSON.parse(
            JSON.stringify(counterpart.doc_attributes),
        );
    }

    counterpart.accounts.forEach((acc) => {
        if (!calculatedAccounts.includes(acc.level_3)) {
            calculatedAccounts.push(acc.level_3);
        }
    });
    calculatedAccounts.push(...counterpart.doc_accounts);

    counterpart.doc_accounts.forEach((level_3) => {
        const account = accounts.find((account) => account.level_3 == level_3);
        if (!!account && !calculatedMotherAccounts.includes(account.level_2)) {
            calculatedMotherAccounts.push(account.level_2);
        }
    });

    const sourceAttributes =
        counterpart.attribute &&
        typeof counterpart.attribute === "object" &&
        !Array.isArray(counterpart.attribute)
            ? counterpart.attribute
            : {};

    Object.keys(sourceAttributes).forEach((key) => {
        if (!calculatedCategories[key]) {
            calculatedCategories[key] = [];
        }
        calculatedCategories[key] = calculatedCategories[key].concat(
            sourceAttributes[key].map((attrib) => attrib.name),
        );
    });

    Object.keys(calculatedCategories).forEach((key) => {
        calculatedCategories[key] = [...new Set(calculatedCategories[key])];
    });

    return {
        ...counterpart,
        calculatedAccounts: [...new Set(calculatedAccounts)],
        calculatedMotherAccounts: [...new Set(calculatedMotherAccounts)],
        calculatedCategories,
    };
}

export default {
    state: () => ({
        // Loading/error flags compartidos por las actions de fetch del store
        // (getDocuments, getAllCounterparts, getAllAccounts...). Las vistas
        // pueden mostrar spinner/banner basado en estos slots — antes los
        // errores se tragaban en .catch() y no había forma de mostrarlos.
        isLoading: false,
        error: null,
        lastDocuments: [],
        allCounterparts: [],
        allOrderedCounterparts: [],
        selectedCounterpart: {},
        allDocumentsByCounterpart: [],
        allOrderedDocumentsByCounterpart: [],
        periodDataSelect: [],
        selectedPeriodCounterparts: { from: null, to: null },
        selectedRange: "",
        allAccounts: [],
        accountsTreeClassification: [],
        AllItemsFirstCategory: [],
        AllItemsSecondCategory: [],
        categories: [],
        // Items por categoría dinámica del backend ({ tiendas: ["Tienda 1",
        // "Tienda 2", …], marcas: [...] }). Lo consume el drawer de Análisis
        // de Ventas para poblar los multi-selects del header. La key del dict
        // matchea exactamente con cada string de `categories`.
        itemsByCategory: {},
        hasNewDocs: false,
        selectedType: "todos",
        typeOptions: [],
        selectedCounterpartObject: { value: "todos", label: "Todos" },
        dataCounterparts: { classified: 0, notClassified: 0, progress: 0 },
        dataDocuments: { classified: 0, notClassified: 0, progress: 0 },
        dataExpenses: { classified: 0, notClassified: 0, progress: 0 },
        selectedHeaderDocsTable: {},
        headersDocsTable: [
            {
                obligationName: "default",
                headers: [
                    {
                        label: "Fecha de Emisión",
                        key: "emitted_at",
                        path: "emitted_at",
                        width: "12",
                        formatDate: "DD-MM-YYYY",
                    },
                    {
                        label: "Mes Devengo",
                        key: "accrual",
                        width: "8",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "N° Doc.",
                        key: "doc_number",
                        path: "doc_number",
                        width: "10",
                    },
                    {
                        label: "Tipo Documento",
                        key: "doc_type",
                        path: "doc_type",
                        width: "15",
                    },
                    {
                        label: "Monto (CLP)",
                        key: "amount",
                        path: "amount",
                        width: "10",
                    },
                    {
                        label: "Cuenta Madre",
                        key: "mother_account",
                        width: "13",
                    },
                    {
                        label: "Cuenta",
                        key: "accounts",
                        width: "17",
                    },
                    {
                        label: "Categorías",
                        key: "attribute",
                        width: "10",
                    },
                ],
            },
            {
                obligationName: "remuneracion-clf",
                headers: [
                    {
                        label: "Mes Trabajado",
                        key: "emitted_at",
                        path: "emitted_at",
                        width: "12",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "Mes Devengo",
                        key: "accrual",
                        width: "8",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "Rut del Trabajador",
                        key: "rut",
                        path: "rut",
                        width: "10",
                    },
                    {
                        label: "Nombre del Trabajador",
                        key: "alias",
                        width: "15",
                        path: "alias",
                    },
                    {
                        label: "Costo Empresa",
                        key: "amount",
                        path: "amount",
                        width: "10",
                    },
                    {
                        label: "Cuenta Madre",
                        key: "mother_account",
                        width: "13",
                    },
                    {
                        label: "Cuenta",
                        key: "accounts",
                        width: "17",
                    },
                    {
                        label: "Categorías",
                        key: "attribute",
                        width: "10",
                    },
                ],
            },
            {
                obligationName: "invoices-clf",
                headers: [
                    {
                        label: "Fecha de Emisión",
                        key: "emitted_at",
                        path: "emitted_at",
                        width: "12",
                        formatDate: "DD-MM-YYYY",
                    },
                    {
                        label: "Mes Devengo",
                        key: "accrual",
                        width: "8",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "Nombre Proveedor",
                        key: "alias",
                        width: "25",
                        path: "alias",
                    },
                    {
                        label: "Monto (CLP)",
                        key: "amount",
                        path: "amount",
                        width: "10",
                    },
                    {
                        label: "Cuenta Madre",
                        key: "mother_account",
                        width: "13",
                    },
                    {
                        label: "Cuenta",
                        key: "accounts",
                        width: "17",
                    },
                    {
                        label: "Categorías",
                        key: "attribute",
                        width: "10",
                    },
                ],
            },
            {
                obligationName: "recibos-clf",
                headers: [
                    {
                        label: "Fecha de Emisión",
                        key: "emitted_at",
                        path: "emitted_at",
                        width: "12",
                        formatDate: "DD-MM-YYYY",
                    },
                    {
                        label: "Mes Devengo",
                        key: "accrual",
                        width: "8",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "Nombre Proveedor",
                        key: "alias",
                        width: "15",
                        path: "alias",
                    },
                    {
                        label: "Detalle Gasto",
                        width: "10",
                        path: "description",
                        key: "description",
                    },
                    {
                        label: "Monto (CLP)",
                        key: "amount",
                        path: "amount",
                        width: "10",
                    },
                    {
                        label: "Cuenta Madre",
                        key: "mother_account",
                        width: "13",
                    },
                    {
                        label: "Cuenta",
                        key: "accounts",
                        width: "17",
                    },
                    {
                        label: "Categorías",
                        key: "attribute",
                        width: "10",
                    },
                ],
            },
            {
                obligationName: "notas-venta-clf",
                headers: [
                    {
                        label: "Fecha de Emisión",
                        key: "emitted_at",
                        path: "emitted_at",
                        width: "12",
                        formatDate: "DD-MM-YYYY",
                    },
                    {
                        label: "Mes Devengo",
                        key: "accrual",
                        width: "8",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "Nombre Cliente",
                        key: "alias",
                        width: "15",
                        path: "alias",
                    },
                    {
                        label: "Detalle Ingreso",
                        width: "10",
                        path: "description",
                        key: "description",
                    },
                    {
                        label: "Monto (CLP)",
                        key: "amount",
                        path: "amount",
                        width: "10",
                    },
                    {
                        label: "Cuenta Madre",
                        key: "mother_account",
                        width: "13",
                    },
                    {
                        label: "Cuenta",
                        key: "accounts",
                        width: "17",
                    },
                    {
                        label: "Categorías",
                        key: "attribute",
                        width: "10",
                    },
                ],
            },
            {
                obligationName: "finiquitos-clf",
                headers: [
                    {
                        label: "Fecha de Emisión",
                        key: "emitted_at",
                        path: "emitted_at",
                        width: "12",
                        formatDate: "DD-MM-YYYY",
                    },
                    {
                        label: "Mes Devengo",
                        key: "accrual",
                        width: "8",
                        formatDate: "MMM-YYYY",
                    },
                    {
                        label: "Rut del Trabajador",
                        key: "rut",
                        path: "rut",
                        width: "10",
                    },
                    {
                        label: "Nombre del Trabajador",
                        key: "alias",
                        width: "15",
                        path: "alias",
                    },
                    {
                        label: "Monto (CLP)",
                        key: "amount",
                        path: "amount",
                        width: "10",
                    },
                    {
                        label: "Cuenta Madre",
                        key: "mother_account",
                        width: "13",
                    },
                    {
                        label: "Cuenta",
                        key: "accounts",
                        width: "17",
                    },
                ],
            },
        ],
    }),

    mutations: {
        SET_LOADING: (state, payload) => {
            state.isLoading = payload;
        },
        SET_ERROR: (state, payload) => {
            state.error = payload;
        },
        SET_LAST_DOCUMENTS: (state, payload) => {
            state.lastDocuments = payload;
        },
        SET_ALL_DOCUMENTS_BY_COUNTERPART: (state, payload) => {
            state.allDocumentsByCounterpart = payload;
        },
        SET_ALL_COUNTERPARTS: (state, payload) => {
            state.allCounterparts = payload;
        },
        SET_ALL_ORDERED_COUNTERPARTS: (state, payload) => {
            state.allOrderedCounterparts = payload;
        },
        SET_SELECTED_COUNTERPART: (state, payload) => {
            state.selectedCounterpart = { ...payload };
        },
        SET_PERIOD_DATA_SELECT: (state, payload) => {
            state.periodDataSelect = payload;
        },
        SET_SELECTED_PERIOD_COUNTERPARTS: (state, payload) => {
            state.selectedPeriodCounterparts = payload;
        },
        SET_SELECTED_RANGE: (state, payload) => {
            state.selectedRange = payload;
        },
        SET_CATEGORIES: (state, payload) => {
            state.categories = payload;
        },
        SET_ITEMS_BY_CATEGORY: (state, payload) => {
            state.itemsByCategory = payload;
        },
        SET_HAS_NEW_DOCS: (state, payload) => {
            state.hasNewDocs = payload;
        },
        SET_ACCOUNTS: (state, payload) => {
            state.allAccounts = payload;
        },
        SET_SELECTED_HEADER_DOCS_TABLE: (state, payload) => {
            state.selectedHeaderDocsTable = payload;
        },
        SET_SELECTED_TYPE: (state, payload) => {
            state.selectedType = payload;
        },
        SET_SELECTED_COUNTERPART_OBJECT: (state, payload) => {
            state.selectedCounterpartObject = payload;
        },
        SET_DATA_CHARTS: (state, payload) => {
            state.dataCounterparts = payload.dataCounterparts;
            state.dataDocuments = payload.dataDocuments;
            state.dataExpenses = payload.dataExpenses;
        },
        SET_TYPE_OPTIONS: (state, payload) => {
            state.typeOptions = payload;
        },
    },
    actions: {
        async getDocuments({ commit }, payload) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            let stale = false;
            try {
                const result = await runLatestDocuments((signal) =>
                    get_documents_involved(
                        payload.ci_id,
                        payload.pay_or_charge,
                        payload.from,
                        payload.to,
                        payload.obligation_name,
                        payload.ledger_only,
                        payload.account_scope,
                        { signal },
                    ),
                );
                stale = result.stale;
                if (stale) {
                    if (result.error) {
                        consoleHandlerError({ error: result.error, prefix: "[classification]", silent: true });
                    }
                    return [];
                }
                const docs = result.value.data["data"].map((doc, i) => ({
                    ...doc,
                    selected: false,
                    index: i,
                    order: i,
                    visible: true,
                }));

                if (payload.type == "last") {
                    commit("SET_LAST_DOCUMENTS", docs);
                } else if (payload.type == "all") {
                    commit("SET_ALL_DOCUMENTS_BY_COUNTERPART", docs);
                }
                return docs;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
                commit("SET_ERROR", extractErrorMessage(error, "Error al cargar documentos."));
                return [];
            } finally {
                if (!stale) commit("SET_LOADING", false);
            }
        },
        setEmptyDocuments({ commit }, payload) {
            if (payload.type == "last") {
                commit("SET_LAST_DOCUMENTS", []);
            } else if (payload.type == "all") {
                commit("SET_ALL_DOCUMENTS_BY_COUNTERPART", []);
            }
        },
        setExtraInfoDocumentsByCounterpart({ commit, state }, payload) {
            const nameCounterpart = payload.alias;
            const rutCounterpart = payload.rut;
            const classifiedAccounts = payload.classifiedAccounts;
            const classifiedAttribs = payload.classifiedAttributes;
            const attributes = payload.attributes;
            const allDocs = state.allDocumentsByCounterpart.map((doc) => {
                const newDoc = JSON.parse(JSON.stringify(doc));
                newDoc.is_classified_by_counterpart = false;
                if (newDoc.accounts.length == 0 && !newDoc.cancelled) {
                    newDoc.accounts = classifiedAccounts.map((classAccount) => {
                        const accountSearched = state.allAccounts.filter(
                            (account) => account.id == classAccount.account,
                        );
                        if (!accountSearched[0]) return null;
                        // No mutar el objeto vivo de state.allAccounts (lo comparten
                        // varios docs y se pisaban el percentage entre sí). Copia.
                        return { ...accountSearched[0], percentage: classAccount.percentage };
                    }).filter(Boolean);

                    classifiedAttribs.map((classAttribute) => {
                        if (!newDoc.attribute[classAttribute.attribute_type]) {
                            newDoc.attribute[classAttribute.attribute_type] =
                                [];
                        }

                        const iAttribute = newDoc.attribute[
                            classAttribute.attribute_type
                        ].findIndex(
                            (attrib) =>
                                attrib.id ===
                                classAttribute.classified_attribute,
                        );
                        if (iAttribute >= 0) return;
                        const itemToAdd = attributes.filter(
                            (attrib) =>
                                attrib.id ==
                                classAttribute.classified_attribute,
                        );
                        newDoc.attribute[classAttribute.attribute_type].push(
                            ...itemToAdd,
                        );
                    });
                    newDoc.is_classified_by_counterpart = true;
                }
                newDoc.mother_accounts = newDoc.accounts.map(
                    (account) => account.level_2,
                );
                newDoc.mother_accounts = newDoc.mother_accounts.filter(
                    (item, index) => {
                        return newDoc.mother_accounts.indexOf(item) === index;
                    },
                );

                if (newDoc.doc_type == "Factura no afecta o exenta") {
                    newDoc.doc_type = "Factura exenta";
                }
                newDoc.alias = nameCounterpart;
                newDoc.rut = rutCounterpart;
                return newDoc;
            });
            commit("SET_ALL_DOCUMENTS_BY_COUNTERPART", allDocs);
        },
        async getAllCounterparts({ commit, state }, payload) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            let stale = false;
            try {
                const result = await runLatestCounterparts((signal) =>
                    get_paginated_counterparts(payload, { signal }),
                );
                stale = result.stale;
                if (stale) {
                    if (result.error) {
                        consoleHandlerError({ error: result.error, prefix: "[classification]", silent: true });
                    }
                    return;
                }
                const { data } = result.value;
                const entries = data["page_data"];
                const counterparts = postProcessCounterparts(
                    entries,
                    state.categories,
                );

                const normalizedCounterparts = counterparts.map((counterpart) =>
                    normalizeCounterpartAccounts(counterpart, state.allAccounts),
                );

                const cloneCounterparts = normalizedCounterparts.map((entry) => {
                    return JSON.parse(JSON.stringify(entry));
                });
                commit("SET_ALL_COUNTERPARTS", normalizedCounterparts);
                commit("SET_ALL_ORDERED_COUNTERPARTS", cloneCounterparts);
                return data;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
                commit("SET_ERROR", extractErrorMessage(error, "Error al cargar contrapartes."));
            } finally {
                if (!stale) commit("SET_LOADING", false);
            }
        },
        // Fetch read-only de una contraparte puntual (deep-link reporte →
        // clasificador). Wrapper fino sobre la api; devuelve `data`.
        async fetchCounterpart(_ctx, { ciId, payOrCharge, from = null, to = null, obligation }) {
            const { data } = await get_counterpart(ciId, payOrCharge, from, to, obligation);
            return data;
        },
        // Prorrateos (cuentas + atributos) de una contraparte para el árbol de
        // clasificación. Wrapper fino; devuelve `data`.
        async fetchProratesByCounterpart(_ctx, { ciId, documentId, payOrCharge, obligation }) {
            const { data } = await get_prorates_x_company_involved(
                ciId, documentId, payOrCharge, obligation,
            );
            return data;
        },
        // Devengo (accrual) por documento+cuenta. Wrappers finos; devuelven `data`.
        // NOTA: el fan-out N+1 (uno por doc×cuenta) sigue del lado del caller
        // (ChangeAccrual.mounted) — colapsarlo a un endpoint batch es S3 (backend).
        async fetchDevengoByCompanyDoc(_ctx, { docId, accountPlanId }) {
            const { data } = await get_devengo_x_company_doc(docId, accountPlanId);
            return data;
        },
        // Batch del rango de devengo: una sola request para todos los pares
        // (doc × cuenta). Devuelve { '<docId>_<accountPlanId>': [accrual...] }.
        async fetchDevengoBatch(_ctx, { docsIds, planAccountsIds }) {
            const { data } = await get_devengo_batch(docsIds, planAccountsIds);
            return data;
        },
        async saveDevengo(_ctx, body) {
            const { data } = await save_devengo(body);
            return data;
        },
        // Detalle completo del devengo de un doc (cada fila: cuenta × mes × %),
        // para el visualizador read-only del mayor. Devuelve [] si no aplica.
        async getDocAccruals(_ctx, docId) {
            const { data } = await get_doc_accruals(docId);
            return data;
        },
        deleteDevengoByDocId(_ctx, { docId, accountPlanId }) {
            return delete_devengo_by_doc_id(docId, accountPlanId);
        },
        // Árbol de prorrateo: guardar / reiniciar. Wrappers finos; el componente
        // arma el `params` y sólo usa el `.then()` (no la data), así que devuelven
        // la promesa tal cual.
        saveProrateTree(_ctx, params) {
            return save_prorate_tree(params);
        },
        deleteProrateByCompanyInvolved(_ctx, params) {
            return delete_prorate_by_company_involved(params);
        },
        async getAllAccounts({ commit }, payload) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            let stale = false;
            try {
                const result = await runLatestAccounts((signal) =>
                    get_account_plan_by_company_ordered(payload, { signal }),
                );
                stale = result.stale;
                if (stale) {
                    if (result.error) {
                        consoleHandlerError({ error: result.error, prefix: "[classification]", silent: true });
                    }
                    return;
                }
                const { data } = result.value;
                commit("SET_ACCOUNTS", data.account_plans);
                return data.account_plans;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
                commit("SET_ERROR", extractErrorMessage(error, "Error al cargar plan de cuentas."));
            } finally {
                if (!stale) commit("SET_LOADING", false);
            }
        },
        orderCounterparts({ commit }, payload) {
            commit("SET_ALL_ORDERED_COUNTERPARTS", payload);
        },
        // Vacía las contrapartes del slot compartido. Lo llaman DashboardClassificator
        // y DashboardErp al montar: como comparten este módulo, arrancar en vacío
        // evita mostrar las contrapartes de la otra vista hasta que llega el fetch
        // propio (con sus params).
        clearCounterparts({ commit }) {
            commit("SET_ALL_COUNTERPARTS", []);
            commit("SET_ALL_ORDERED_COUNTERPARTS", []);
        },
        setSelectedCounterpart({ commit }, payload) {
            commit("SET_SELECTED_COUNTERPART", payload);
        },
        setPeriodDataSelect({ commit }, payload) {
            commit("SET_PERIOD_DATA_SELECT", payload);
        },
        async getCategories({ commit, state }) {
            // Short-circuit: una sola request por sesión. El short-circuit
            // mira `categories` (el slot original); si está poblado asumimos
            // que `itemsByCategory` también — la action los commitea siempre
            // como par.
            // OJO multi-empresa: las categorías son por-empresa, pero cambiar
            // de empresa activa hace window.location.reload() (userAuth/
            // setActiveCompany), que reinicia este state a [] y fuerza el
            // re-fetch para la empresa nueva. Si el cambio de empresa pasa a
            // ser reactivo (sin reload), este short-circuit debe removerse o
            // keyarse por empresa, o quedarían categorías de la empresa previa.
            if (state.categories.length > 0) return;
            try {
                const { data } = await getCategories();
                commit("SET_CATEGORIES", data["types"] || []);
                commit("SET_ITEMS_BY_CATEGORY", data["items_by_type"] || {});
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
                commit("SET_ERROR", extractErrorMessage(error, "Error al cargar categorías."));
            }
        },
        async getIfHasNewDocs({ commit }) {
            try {
                const { data } = await hasNewDocsByCompany();
                commit("SET_HAS_NEW_DOCS", data.has_new_tax_docs);
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
            }
        },
        setSelectedPeriodCounterparts({ commit, dispatch }, payload) {
            commit("SET_SELECTED_PERIOD_COUNTERPARTS", payload);
            commit("SET_SELECTED_RANGE", payload.value);
            dispatch("updateDataCharts");
        },
        async updateCounterpart({ commit, state }, payload) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            let stale = false;
            try {
                const result = await runLatestCounterpartUpdate((signal) =>
                    get_counterpart(
                        payload.ci_id,
                        payload.pay_or_charge,
                        state.selectedPeriodCounterparts.from,
                        state.selectedPeriodCounterparts.to,
                        payload.obligation_name,
                        { signal },
                    ),
                );
                stale = result.stale;
                if (stale) {
                    if (result.error) {
                        consoleHandlerError({ error: result.error, prefix: "[classification]", silent: true });
                    }
                    return;
                }
                const { data } = result.value;
                // Copia shallow del array (no deep-clone de toda la lista): sólo
                // reemplazamos UNA entrada por una recién normalizada; las demás
                // ya son objetos aislados de allCounterparts (clonados al fetch),
                // así que reusar sus refs es seguro y evita clonar N entradas.
                const allOrdered = state.allOrderedCounterparts.slice();
                const counterpart = data["page_data"][0];
                const iEntrySearched = allOrdered.findIndex(
                    (entrySearched) =>
                        payload.ci_id == entrySearched.ci_id &&
                        payload.pay_or_charge == entrySearched.pay_or_charge &&
                        payload.obligation_name ==
                            entrySearched.obligation_name,
                );
                const postCounterpart = postProcessCounterparts(
                    [counterpart],
                    state.categories,
                )[0];

                const normalizedCounterpart = normalizeCounterpartAccounts(
                    postCounterpart,
                    state.allAccounts,
                );
                allOrdered.splice(iEntrySearched, 1, normalizedCounterpart);
                commit("SET_ALL_ORDERED_COUNTERPARTS", allOrdered);
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
                commit("SET_ERROR", extractErrorMessage(error, "Error al actualizar contraparte."));
            } finally {
                if (!stale) commit("SET_LOADING", false);
            }
        },
        setOrderedCounterparts({ commit }, payload) {
            commit("SET_ALL_ORDERED_COUNTERPARTS", payload);
        },
        setSelectedHeaderDocTable({ commit, state }, payload) {
            let selected = state.headersDocsTable.find(
                (headerDocsTable) => headerDocsTable.obligationName == payload,
            );
            if (!selected) {
                selected = state.headersDocsTable[0];
            }
            commit("SET_SELECTED_HEADER_DOCS_TABLE", selected);
        },
        updateSelectedType({ commit, dispatch }, payload) {
            commit("SET_SELECTED_TYPE", payload.value);
            commit("SET_SELECTED_COUNTERPART_OBJECT", payload);
            dispatch("updateDataCharts");
        },
        updateDataCharts({ commit, state }) {
            let classifiedCounterpart = 0;
            let notClassifiedCounterpart = 0;
            let classifiedDocuments = 0;
            let notClassifiedDocuments = 0;
            let classifiedAmount = 0;
            let notClassifiedAmount = 0;

            const selectedType = state.selectedType;

            state.allOrderedCounterparts.forEach((entry) => {
                const isValidTotal =
                    entry.total_3_months !== 0 && entry.total_3_months !== "";
                const isMatchingType =
                    selectedType === "todos" ||
                    selectedType === entry.counterpart_type;

                if (isValidTotal && isMatchingType) {
                    const classifyLower =
                        entry.classify_by_counterpart.toLowerCase();
                    let isClassified = false;

                    if (
                        classifyLower !== "sin clasificar" &&
                        classifyLower !== "documento"
                    ) {
                        isClassified = true;
                    } else if (
                        classifyLower === "documento" &&
                        entry.classified === entry.tax_document_count
                    ) {
                        isClassified = true;
                    }

                    if (isClassified) {
                        classifiedCounterpart++;
                    } else {
                        notClassifiedCounterpart++;
                    }

                    classifiedDocuments += entry.classified;
                    notClassifiedDocuments +=
                        entry.tax_document_count - entry.classified;

                    if (
                        (selectedType !== "Cliente" &&
                            entry.pay_or_charge === "P") ||
                        (selectedType === "Cliente" &&
                            entry.pay_or_charge === "C")
                    ) {
                        if (isClassified) {
                            classifiedAmount += entry.total_3_months;
                        } else {
                            notClassifiedAmount += entry.total_3_months;
                        }
                    }
                }
            });

            const data = {
                dataCounterparts: {
                    classified: classifiedCounterpart,
                    notClassified: notClassifiedCounterpart,
                    progress:
                        parseInt(
                            (classifiedCounterpart /
                                (classifiedCounterpart +
                                    notClassifiedCounterpart)) *
                                100,
                        ) || 0,
                },
                dataDocuments: {
                    classified: classifiedDocuments,
                    notClassified: notClassifiedDocuments,
                    progress:
                        parseInt(
                            (classifiedDocuments /
                                (classifiedDocuments +
                                    notClassifiedDocuments)) *
                                100,
                        ) || 0,
                },
                dataExpenses: {
                    classified: classifiedAmount,
                    notClassified: notClassifiedAmount,
                    progress:
                        parseInt(
                            (classifiedAmount /
                                (classifiedAmount + notClassifiedAmount)) *
                                100,
                        ) || 0,
                },
            };
            commit("SET_DATA_CHARTS", data);
        },
        initClassificationFilters({ commit, dispatch, rootState }) {
            const date = new Date();
            const toDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
            
            const rangeOptions = [
                {
                    value: "mesActual",
                    label: "Mes Actual",
                    from: new Date(date.getFullYear(), date.getMonth(), 1),
                    to: toDate,
                },
                {
                    value: "mesAnterior",
                    label: "Mes Anterior",
                    from: new Date(date.getFullYear(), date.getMonth() - 1, 1),
                    to: new Date(date.getFullYear(), date.getMonth(), 1),
                },
                {
                    value: "trimActual",
                    label: "Trimestre actual",
                    from: new Date(date.getFullYear(), date.getMonth() - 3, 1),
                    to: toDate,
                },
                {
                    value: "anioActual",
                    label: "Año Actual",
                    from: new Date(date.getFullYear(), 0, 1),
                    to: toDate,
                },
                {
                    value: "anioPasado",
                    label: "Año Pasado",
                    from: new Date(date.getFullYear() - 1, 0, 1),
                    to: new Date(date.getFullYear() - 1, 11, 31),
                },
                {
                    value: "last12",
                    label: "Últimos 12 meses",
                    from: new Date(date.getFullYear(), date.getMonth() - 12, 1),
                    to: toDate,
                },
                {
                    value: "last24",
                    label: "Últimos 24 meses",
                    from: new Date(date.getFullYear(), date.getMonth() - 24, 1),
                    to: toDate,
                },
                {
                    value: "todo",
                    label: "Toda la historia",
                    from: new Date(2015, 0, 1),
                    to: toDate,
                },
            ];
            commit("SET_PERIOD_DATA_SELECT", rangeOptions);

            const user = rootState.userAuth.user;
            const allCounterpartsType = [
                { value: "Cliente", label: "🤝 Cliente" },
                { value: "Proveedor", label: "💼 Proveedor" },
                { value: "Honorario", label: "🕒 Honorario" },
                { value: "Trabajador", label: "🧑‍💻 Trabajador" },
                { value: "Internacional", label: "🌍 Internacional" },
                { value: "Recibo", label: "🧾 Recibo" },
                { value: "Finiquito", label: "📄 Finiquito" },
                // value = string almacenado en user.valid_counterparts (BD);
                // solo el label muestra "Ingreso".
                { value: "Nota de Venta", label: "💵 Nota de Ingreso" },
            ];
            const typeOptions = allCounterpartsType.filter((type) => {
                return (
                    !user ||
                    user.valid_counterparts.length == 0 ||
                    user.valid_counterparts.includes(type.value)
                );
            });
            commit("SET_TYPE_OPTIONS", [{ value: "todos", label: "Todos" }, ...typeOptions]);

            dispatch("getCategories");
        },
    },
    namespaced: true,
};
